// **Watching a practice run while it is still being written** — the main thread's half
// (`rounds-practice-sandbox` task 5.13; `rounds-trace`'s in-process carve-out, design **D-D**;
// `client-practice` "Watching begins before the run completes").
//
// The worker writes the run's season into origin-private storage and, on a bounded cadence, hands
// this thread the extent it has **already written** as decoded world state
// (`PracticeInFlightView`, whose doc comment carries the whole reading of "outside the writing
// process" and the four clauses the crossing keeps). This file accumulates those deltas into one
// growing view and adapts it into the **shipped** `ReviewTrace` seam, so watching a run before it
// finishes is the ordinary review surface doing its ordinary job rather than a second renderer that
// could disagree with it.
//
// ## Three properties, and none of them is a comment asking the reader to be careful
//
// **Nothing here is durable.** The accumulator is a closure over two collections. It writes to no
// store — not the origin-private one, not web storage, not a database — and holds no handle to
// anything that could. That is asserted by a **source scan** in `practice-watch.test.tsx`, which is
// why the sink names are deliberately not spelled out in this comment: "we did not do that" is worth
// a test when the requirement is that the extent "remains **absent** across a restart", and a test
// that its own prose defeats is worth nothing. A reload starts a fresh watcher over a fresh
// coordinator whose world is empty, and the writer's store reclaims the generation before it serves
// any read.
//
// **Nothing here can be mistaken for a published trace.** The prefix it rebuilds is branded
// `in-process-unverified` (`inFlightPrefixFromSnapshot`) and the trace it produces is held in a
// world arm of the coordinator that is deliberately not `loaded`.
//
// **A view for another run is dropped, not merged.** One watcher per run: a late delta from the
// previous run must never be drawn under the new one, which is the same crossed-pairing rule the
// coordinator's epoch checks enforce one layer up.

import type { CoarseSample, DetailRecord, InFlightPrefix } from '../../net/rounds-trace';
import { reviewTraceFromInFlight } from '../../net/rounds-review-source';
import type { ReviewTrace } from '../../net/rounds-review-source';
import type { PracticeInFlightView } from './practice-runner';
import { HIVE_PER_CELL_CAPACITY, SIM_TICK_RATE_HZ } from '../../generated/sim-constants';

/** The accumulating view of one run's written extent. */
export interface PracticeWatcher {
  /** The run this watcher accumulates. */
  readonly runId: number;
  /**
   * How many record **arrivals** this watcher has folded, ever — the work counter, in the same spirit
   * as `InFlightPrefix.recordsReplayed`: the bound is measurable rather than something a test has to
   * infer by reading the algorithm.
   *
   * It equals the number of records that have *arrived*, not the number of distinct ticks held: a
   * re-delivered delta is folded again, cheaply, and counted again. That is the useful reading, because
   * the property under test is "a fold costs its own delta and never re-walks the run" — the shape this
   * replaced rebuilt its whole prefix per view, so carrying 24 records over four views cost 60 touches.
   * For "did this fold make the extent bigger", the signal is
   * {@link ReviewTrace.extentVersion} changing, not this counter moving.
   */
  readonly recordsFolded: number;
  /**
   * Folds a delta in and returns the trace to draw, or `null` when the view belongs to another run.
   *
   * **The same `ReviewTrace` every time, grown in place.** This used to be a fresh object per call,
   * on the reasoning that "the coordinator stores it in state and React re-renders off identity, so a
   * view that grew in place would draw the first frame and then never update". The first half is true
   * and the conclusion does not follow: the reducer already produces a fresh state and a fresh `world`
   * for every `run-watched`, so the re-render happens whatever this returns. What the fresh trace
   * actually bought was a **full renderer sync per delta** — `ReviewFrame` names `trace` in its draw
   * effect's dependencies — plus a recomputed colony fit on the screen above it, both on top of the
   * re-render, and that bundle is the 108-155 ms frames watching was measured to spike at.
   *
   * Growth is stated as {@link ReviewTrace.extentVersion} instead, which the frame names in place of
   * the identity. So a delta that does not move the drawn picture now costs nothing, and one that
   * does still redraws — including for a *paused* watcher, which has no advancing tick to redraw for
   * it.
   */
  observe(view: PracticeInFlightView): ReviewTrace | null;
}

/** The index of the last sample at-or-before `tick`, or `-1`. Binary search rather than the linear
 * scan `inFlightPrefixFromSnapshot`'s own `coarseAt` does: that one is written for a prefix built
 * fresh per call, whereas this is asked **per drawn frame** for the whole life of a run. */
function coarseIndexAtOrBefore(samples: readonly CoarseSample[], tick: number): number {
  let lo = 0;
  let hi = samples.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (samples[mid]!.tick <= tick) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return found;
}

/** Inserts `tick` into an ascending array, in place. The overwhelmingly common case is an append —
 * the writer emits in order — so that is the fast path; anything else is placed by a backward walk,
 * which is what keeps a repeated or out-of-order delta from duplicating a tick or leaving the array
 * unsorted. */
function insertTick(ticks: number[], tick: number): void {
  const last = ticks.length - 1;
  if (last < 0 || tick > ticks[last]!) {
    ticks.push(tick);
    return;
  }
  let i = last;
  while (i >= 0 && ticks[i]! > tick) {
    i -= 1;
  }
  if (i >= 0 && ticks[i] === tick) {
    return;
  }
  ticks.splice(i + 1, 0, tick);
}

/**
 * Builds a watcher for one run.
 *
 * `runId` is taken up front rather than adopted from the first view, so a watcher constructed for the
 * run the player just started cannot silently become a watcher for an older one whose last delta
 * happened to arrive later.
 */
export function createPracticeWatcher(runId: number): PracticeWatcher {
  /** Records by tick — a map rather than an array, so an out-of-order or repeated delta folds in
   * idempotently instead of duplicating a tick or leaving a hole. */
  const records = new Map<number, DetailRecord>();
  /** The same ticks, ascending. Maintained alongside the map rather than derived from it, because
   * deriving it is what made a fold cost the whole run: `[...map.keys()].sort()` per delta. */
  const ticks: number[] = [];
  let coarseSamples: readonly CoarseSample[] = [];
  /** Match-static, so the first report that carries one settles it — a later empty table is the
   * writer not having flushed its preamble yet, never the run having lost its state names. */
  let stateNames: readonly string[] = [];
  let recordsFolded = 0;
  let extentVersion = 0;
  /**
   * The one prefix, reading the live collections above.
   *
   * Its `ticks`/`coarseSamples` expose the accumulator's own arrays. That is deliberate and it is why
   * this prefix is **private to this closure**: `InFlightPrefix` declares them `readonly`, which stops
   * a consumer writing to them but says nothing about them growing, and the only consumer here is
   * `reviewTraceFromInFlight` — which reads `writeHeadTick` and the two lookups, and never the arrays.
   * Nothing outside this function is handed the prefix.
   */
  const prefix: InFlightPrefix = {
    verification: 'in-process-unverified',
    ticks,
    coarseSamples,
    stateNames,
    firstTick: null,
    writeHeadTick: null,
    coarseAt(tick: number): CoarseSample | null {
      const at = coarseIndexAtOrBefore(coarseSamples, tick);
      return at < 0 ? null : coarseSamples[at]!;
    },
    detailAt(tick: number): DetailRecord | null {
      return records.get(tick) ?? null;
    },
    // Always 0: this side never decodes anything. The replay already happened once, in the writer's
    // own `decodeInFlightPrefix`, before the records crossed as an `InFlightSnapshot` — this is a
    // lookup over what arrived, not a second decode of it.
    recordsReplayed: 0,
  };
  /** The one trace, grown in place — see {@link PracticeWatcher.observe}. Built on the first fold,
   * because `fieldSize` and the horizon are the view's to state and there is nothing honest to put in
   * either before one arrives. */
  let trace: ReviewTrace | null = null;

  return {
    runId,
    get recordsFolded(): number {
      return recordsFolded;
    },
    observe(view: PracticeInFlightView): ReviewTrace | null {
      if (view.runId !== runId) {
        return null;
      }
      // `grew` is what {@link ReviewTrace.extentVersion} is bumped from, and it is deliberately
      // "this fold made the extent bigger" rather than "this fold happened". Bumping per call would
      // move the churn from the identity to the version and change nothing: a repeated delta, or a
      // report carrying only coarse, would still fire the frame's full renderer sync.
      let grew = 0;
      for (const record of view.snapshot.records) {
        if (!records.has(record.tick)) {
          insertTick(ticks, record.tick);
          grew += 1;
        }
        records.set(record.tick, record);
        recordsFolded += 1;
      }
      // The coarse tier arrives whole each time (it is small, and `coarseAt` answers over the whole
      // of it), so the newest report replaces rather than merges — but never *shrinks* the tier: a
      // report that somehow carried fewer samples than one already in hand would silently retract
      // coarse the player has already been shown.
      //
      // Sorted on adoption rather than searched unsorted: `coarseAt` is a binary search now, and the
      // tier is a few dozen samples at the coarse cadence, so one sort per delta is nothing next to
      // the per-frame lookups it makes correct.
      //
      // **The length comparison is a growth test, and it is sound only because the coarse tier is
      // append-only.** The writer emits one sample per coarse-cadence tick and never rewrites an
      // earlier one (`inFlightSnapshotOf` hands over the whole tier as it stands), so "more samples"
      // and "more coarse" are the same statement and an equal-length report carries identical content.
      // If the writer ever revised a sample in place, this would miss it: the growth signal would have
      // to compare content, not count. Named rather than assumed, because the coupling is invisible
      // from this side of the thread boundary.
      if (view.snapshot.coarseSamples.length > coarseSamples.length) {
        coarseSamples = [...view.snapshot.coarseSamples].sort((a, b) => a.tick - b.tick);
        prefix.coarseSamples = coarseSamples;
        // Coarse counts as growth: the frame falls back to the coarse tier wherever detail is not
        // resident, so a new sample can change the drawn picture even when no detail arrived.
        grew += 1;
      }
      // The state-name table is match-static and arrives with the writer's preamble, which may be
      // later than the first record. Adopting it counts as growth: labels appearing under the bees
      // changes the drawn picture even when no new tick did.
      if (stateNames.length === 0 && view.snapshot.stateNames.length > 0) {
        stateNames = view.snapshot.stateNames;
        prefix.stateNames = stateNames;
        grew += 1;
      }
      prefix.firstTick = ticks.length === 0 ? null : ticks[0]!;
      prefix.writeHeadTick = ticks.length === 0 ? null : ticks[ticks.length - 1]!;
      if (grew > 0) {
        extentVersion += 1;
      }

      if (trace === null) {
        trace = reviewTraceFromInFlight(prefix, {
          // `fieldSize`/`expectedTerminalTick` are required by the adapter and neither guessable
          // here — see `InFlightTraceOptions`. They come from the run's own engine, carried on the
          // view. `tickRateHz` has no such per-run field to carry: this is the device-side practice
          // path (**fallback-permitted**, client-tick-rate-provenance / tick-rate-single-source task
          // 5.4) — the run executes in `waggle-wasm`, compiled from the same `Balance::seed()` the
          // generated mirror is generated from, so the mirror provably IS the running rate.
          // `perCellCapacity` (WAG-100) is sourced the same way and on exactly the same grounds:
          // it is this device's own engine's constant, not a claim about someone else's run.
          fieldSize: view.fieldSize,
          expectedTerminalTick: view.expectedTerminalTick,
          tickRateHz: SIM_TICK_RATE_HZ,
          perCellCapacity: HIVE_PER_CELL_CAPACITY,
        });
      } else {
        // The two fields that are values rather than lookups, so they are the two that have to be
        // refreshed by hand. The adapter derives `terminalTick` the same way — the horizon, or the
        // write head once a run overruns it — and re-deriving it here rather than re-adapting keeps
        // the whole fold O(delta).
        trace.terminalTick = Math.max(view.expectedTerminalTick, prefix.writeHeadTick ?? 0);
        // Same reason as `terminalTick`: the adapter reads `prefix.stateNames` once, so a table that
        // arrives after the first fold has to be pushed onto the grown trace by hand.
        trace.stateNames = stateNames;
      }
      trace.extentVersion = extentVersion;
      return trace;
    },
  };
}
